.grid {
  align-items: stretch;
}
@media (min-width: 64rem) {
  .grid.grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* ---- shared pieces ------------------------------------------------------- */
.stars {
  display: flex;
  gap: 0.2em;
  font-size: var(--fs-base);
  color: var(--accent);
}
.star {
  width: 1em;
  height: 0.95em;
  fill: currentColor;
}

.avatar {
  flex: none;
  width: 6rem;
  height: 6rem;
  border-radius: 15%;
  overflow: hidden;
  background: var(--surface-hover);
  box-shadow: 0 0 0 1px var(--border-strong);
}
.avatarLg {
  width: 8.5rem;
  height: 8.5rem;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--accent);
}
.avatarLogo {
  /* An agency mark gets the same plate as a portrait: wide logos and detailed seals
     need the corners. The marks are white, so a light surface keeps this plate dark. */
  display: grid;
  place-items: center;
  background: var(--logo-plate, var(--surface-hover));
}
.avatarLogo img {
  /* Percentages here resolve against the avatar slot; padding on .avatarLogo would
     resolve against the card and swallow the whole mark. */
  width: 90%;
  height: 100%;
  object-fit: contain;
}

.name {
  margin: 0;
  font-weight: 650;
  color: var(--text-strong);
}
.role,
.org,
.project {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
.org {
  color: var(--text);
}
.project {
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  margin-top: var(--sp-1);
}
.project::before {
  content: "";
  flex: none;
  width: 2px;
  align-self: stretch;
  background: var(--accent);
}

.links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-5);
  margin: 0;
  font-size: var(--fs-sm);
}
.link {
  color: var(--link);
  text-underline-offset: 3px;
}
.link[data-video="true"]::before {
  content: "";
  display: inline-block;
  width: 0;
  height: 0;
  margin-right: 0.5em;
  border: 0.4em solid transparent;
  border-left-color: var(--accent);
  border-right-width: 0;
  vertical-align: -0.05em;
}

/* ---- card ---------------------------------------------------------------- */
.cell {
  display: flex;
}
.card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding: var(--sp-6);
  border-radius: var(--radius-lg);
  background: var(--surface-raised);
  border: 1px solid var(--border);
}
.quote {
  margin: 0;
  flex: 1;
  font-size: var(--fs-md);
  line-height: var(--lh-snug);
  color: var(--text-strong);
}
.quote p {
  margin: 0;
}
.clamped .quote p {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 9;
  line-clamp: 9;
}
.who {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  padding-top: var(--sp-4);
  border-top: 1px solid var(--border);
}
.whoText {
  min-width: 0;
}

/* ---- row: a list read top to bottom, the speaker beside the quote ------------ */
.row {
  display: grid;
  gap: var(--sp-5);
  padding-block: var(--sp-8);
  border-top: 1px solid var(--border-strong);
}
.rowWho {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
}
/* A portrait-shaped face beside the name, a little taller than it is wide. */
.rowWho > .avatar {
  width: 6.5rem;
  height: 7.5rem;
}
.rowBody {
  display: grid;
  gap: var(--sp-4);
}
.rowQuote {
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(var(--fs-lg), 0.6vw + 1rem, var(--fs-xl));
  font-weight: 500;
  line-height: var(--lh-snug);
  letter-spacing: -0.01em;
  color: var(--text-strong);
  text-wrap: pretty;
}
.rowQuote p {
  margin: 0;
  max-width: 52rem;
}
@media (min-width: 48rem) {
  .row {
    grid-template-columns: minmax(0, 19rem) minmax(0, 1fr);
    gap: var(--sp-12);
    align-items: start;
  }
}

@media (max-width: 40rem) {
  .avatarLg {
    width: 6rem;
    height: 6rem;
  }
}

/* ---- stat card: a testimonial that states a number ------------------------- */
.statCard {
  border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
  background: color-mix(in srgb, var(--accent) 10%, var(--surface-raised));
}
.stat {
  display: grid;
  gap: var(--sp-1);
  margin: 0;
}
.statValue {
  font-family: var(--font-display);
  font-size: clamp(3.5rem, 2rem + 3vw, 5rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: var(--tracking-display);
  color: var(--accent-text);
}
.statLabel {
  font-size: var(--fs-lg);
  font-weight: 600;
  line-height: var(--lh-snug);
  color: var(--text-strong);
}
.statQuote {
  flex: 1;
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text);
}
.statQuote p {
  margin: 0;
}

/* ---- spotlight card: the one quote a row leads with ------------------------ */
.spotlight {
  flex-direction: row;
  align-items: stretch;
  gap: var(--sp-6);
}
.spotlightBody {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
  min-width: 0;
}
.spotlightQuote {
  flex: 1;
  margin: 0;
  font-size: clamp(var(--fs-lg), 0.9rem + 1vw, var(--fs-2xl));
  line-height: var(--lh-tight);
  letter-spacing: -0.015em;
  color: var(--text-strong);
  text-wrap: balance;
}
.spotlightQuote p {
  margin: 0;
}
.spotlightWho .name {
  font-size: var(--fs-md);
}
.spotlightFigure {
  flex: none;
  align-self: flex-end;
}
@media (max-width: 47.99rem) {
  .spotlight {
    flex-direction: column-reverse;
  }
  .spotlightFigure {
    align-self: flex-start;
  }
}
